/* MapLibre style comparator (MapTiler custom-style selection).
 *
 * Authed verification surface at /map-preview — NOT linked in any nav.
 * Founder brief: high-quality dark style with readable streets,
 * buildings and water; cyan ONLY as DealAlert accent (markers,
 * clusters, selection, heatmap). Compare style variants across
 * country → city → street zoom before adopting one in production.
 */
import React, { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { MAPTILER_KEY, maptilerStyleUrl } from "../config/mapProvider";

const CYAN = "#22d3ee";

const STYLE_VARIANTS = [
  { id: "basic-v2-dark", label: "Basic Dark (Basis)", hint: "Produktionsbasis — ruhig, Marker im Vordergrund" },
  {
    id: "dealalert-tuned",
    base: "basic-v2-dark",
    tuned: true,
    label: "DealAlert Tuned",
    hint: "Basic Dark + kontrastreichere Gebäude + schärfere Labels",
  },
  { id: "streets-v2-dark", label: "Streets Dark", hint: "Detailreich — Straßen, Gebäude, POIs" },
  { id: "dataviz-dark", label: "Dataviz Dark", hint: "Reduziert — optimiert für Daten-Overlays" },
  { id: "backdrop-dark", label: "Backdrop Dark", hint: "Monochrom — maximal dezenter Hintergrund" },
];

// Custom-style direction (founder brief): buildings a bit more
// contrasty, labels/streets crisp — NO cyan in the base map. These
// overrides document exactly what the MapTiler-editor custom style
// should change relative to basic-v2-dark.
function applyDealAlertTuning(map) {
  const safe = (fn) => { try { fn(); } catch { /* layer absent in style */ } };
  safe(() => map.setPaintProperty("Building", "fill-color", "#2f333d"));
  safe(() => map.setPaintProperty("Building", "fill-outline-color", "#41465280"));
  safe(() => map.setPaintProperty("Water", "fill-color", "#1b2733"));
  ["Road labels", "Place labels", "City labels", "Station labels"].forEach((lid) => {
    safe(() => map.setPaintProperty(lid, "text-halo-color", "#0b0d10"));
    safe(() => map.setPaintProperty(lid, "text-halo-width", 1.4));
    safe(() => map.setPaintProperty(lid, "text-color", "#c8ccd4"));
  });
}

const ZOOM_PRESETS = [
  { id: "country", label: "Land", center: [14.7, 47.7], zoom: 6.2 },
  { id: "city", label: "Stadt", center: [16.3738, 48.2082], zoom: 12.4 },
  { id: "street", label: "Straße", center: [16.371, 48.2075], zoom: 15.8 },
];

// Deterministic demo deals: dense cluster around Vienna + spread across AT.
function buildDemoPoints() {
  const pts = [];
  const seedAround = (lng, lat, n, spread) => {
    for (let i = 0; i < n; i += 1) {
      const a = (i * 137.508 * Math.PI) / 180; // golden angle — stable spiral
      const r = spread * Math.sqrt((i + 1) / n);
      pts.push([lng + r * Math.cos(a), lat + r * Math.sin(a) * 0.7]);
    }
  };
  seedAround(16.3738, 48.2082, 34, 0.05); // Vienna dense
  seedAround(15.4395, 47.0707, 10, 0.03); // Graz
  seedAround(14.2858, 48.3069, 8, 0.03); // Linz
  seedAround(13.055, 47.8095, 6, 0.02); // Salzburg
  return {
    type: "FeatureCollection",
    features: pts.map(([lng, lat], i) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [lng, lat] },
      properties: { selected: i === 2 },
    })),
  };
}
const DEMO_POINTS = buildDemoPoints();

function addDealLayers(map) {
  map.addSource("demo-deals", {
    type: "geojson",
    data: DEMO_POINTS,
    cluster: true,
    clusterRadius: 46,
    clusterMaxZoom: 13,
  });
  // Country-level heatmap accent (fades out towards city zoom).
  map.addLayer({
    id: "deal-heat",
    type: "heatmap",
    source: "demo-deals",
    maxzoom: 10,
    paint: {
      "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 5, 16, 9, 28],
      "heatmap-intensity": 0.9,
      "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 8, 0.75, 10, 0],
      "heatmap-color": [
        "interpolate", ["linear"], ["heatmap-density"],
        0, "rgba(34,211,238,0)",
        0.3, "rgba(34,211,238,0.25)",
        0.6, "rgba(34,211,238,0.55)",
        1, "rgba(103,232,249,0.9)",
      ],
    },
  });
  map.addLayer({
    id: "deal-clusters",
    type: "circle",
    source: "demo-deals",
    filter: ["has", "point_count"],
    paint: {
      "circle-radius": ["step", ["get", "point_count"], 15, 10, 19, 25, 24],
      "circle-color": "rgba(34,211,238,0.22)",
      "circle-stroke-width": 2,
      "circle-stroke-color": CYAN,
    },
  });
  map.addLayer({
    id: "deal-cluster-count",
    type: "symbol",
    source: "demo-deals",
    filter: ["has", "point_count"],
    layout: {
      "text-field": ["get", "point_count_abbreviated"],
      "text-size": 12,
      "text-font": ["Noto Sans Bold"],
    },
    paint: { "text-color": "#a5f3fc" },
  });
  map.addLayer({
    id: "deal-markers",
    type: "circle",
    source: "demo-deals",
    filter: ["!", ["has", "point_count"]],
    paint: {
      "circle-radius": 7,
      "circle-color": CYAN,
      "circle-stroke-width": 2,
      "circle-stroke-color": "#0e7490",
    },
  });
  // Selection accent — bigger glow ring on the selected deal.
  map.addLayer({
    id: "deal-selected-ring",
    type: "circle",
    source: "demo-deals",
    filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "selected"], true]],
    paint: {
      "circle-radius": 14,
      "circle-color": "rgba(34,211,238,0.18)",
      "circle-stroke-width": 2.5,
      "circle-stroke-color": "#67e8f9",
    },
  });
}

export default function MapLibrePreviewPage() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [styleId, setStyleId] = useState(STYLE_VARIANTS[0].id);
  const [preset, setPreset] = useState(ZOOM_PRESETS[1].id);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!MAPTILER_KEY || !containerRef.current) return undefined;
    setStatus("loading");
    const variant = STYLE_VARIANTS.find((s) => s.id === styleId) || STYLE_VARIANTS[0];
    const start = ZOOM_PRESETS.find((p) => p.id === preset) || ZOOM_PRESETS[1];
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: maptilerStyleUrl(variant.base || variant.id),
      center: start.center,
      zoom: start.zoom,
      attributionControl: { compact: true },
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.on("load", () => {
      if (variant.tuned) applyDealAlertTuning(map);
      addDealLayers(map);
      setStatus("ready");
    });
    map.on("error", (e) => {
      if (e?.error?.status === 404 || !map.isStyleLoaded()) setStatus("error");
    });
    return () => {
      mapRef.current = null;
      map.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleId]);

  const jumpTo = (p) => {
    setPreset(p.id);
    mapRef.current?.jumpTo({ center: p.center, zoom: p.zoom });
  };

  if (!MAPTILER_KEY) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-16 text-center" data-testid="maplibre-preview-nokey">
        <h1 className="text-xl font-semibold text-zinc-100">MapLibre Preview</h1>
        <p className="mt-3 text-sm text-zinc-400">
          REACT_APP_MAPTILER_KEY ist nicht gesetzt — Vorschau deaktiviert.
        </p>
      </div>
    );
  }

  const active = STYLE_VARIANTS.find((s) => s.id === styleId);

  return (
    <div className="mx-auto max-w-6xl px-5 py-6 space-y-4" data-testid="maplibre-preview-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">DealAlert Karten-Style-Vergleich</h1>
          <p className="text-xs text-zinc-500">
            MapTiler-Basis-Styles + Cyan nur als DealAlert-Akzent (Marker · Cluster · Selektion · Heatmap)
          </p>
        </div>
        <span
          data-testid="maplibre-preview-status"
          className={`rounded-full border px-3 py-1 text-xs font-medium ${
            status === "ready"
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : status === "error"
                ? "border-red-500/40 bg-red-500/10 text-red-300"
                : "border-zinc-700 bg-zinc-900 text-zinc-400"
          }`}
        >
          {status === "ready" ? "Vector-Style geladen" : status === "error" ? "Style-Fehler" : "Lädt…"}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="style-variant-switcher">
        {STYLE_VARIANTS.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`style-variant-${s.id}`}
            onClick={() => setStyleId(s.id)}
            className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
              styleId === s.id
                ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-300"
                : "border-zinc-700 bg-zinc-900/70 text-zinc-300 hover:border-zinc-500"
            }`}
          >
            {s.label}
          </button>
        ))}
        <span className="ml-1 text-[11px] text-zinc-500">{active?.hint}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="zoom-preset-switcher">
        {ZOOM_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`zoom-preset-${p.id}`}
            onClick={() => jumpTo(p)}
            className={`rounded-lg border px-4 py-1.5 text-xs font-medium transition-colors ${
              preset === p.id
                ? "border-zinc-400 bg-zinc-800 text-zinc-100"
                : "border-zinc-700 bg-zinc-900/70 text-zinc-400 hover:border-zinc-500"
            }`}
          >
            {p.label}
          </button>
        ))}
        <span className="ml-1 text-[11px] text-zinc-500">Land = Heatmap · Stadt = Cluster · Straße = Marker + Selektion</span>
      </div>

      <div
        ref={containerRef}
        data-testid="maplibre-preview-map"
        className="h-[560px] w-full overflow-hidden rounded-2xl border border-zinc-800"
      />
    </div>
  );
}
